import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, Route as RouteIcon, RefreshCw, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const DEFAULT_REASON = 'demande créée par erreur — course non effectuée';

const STATUSES = {
  pending: { label: 'En attente', color: 'bg-zinc-700 text-zinc-100' },
  accepted: { label: 'Acceptée', color: 'bg-blue-500/20 text-blue-300 border border-blue-500/40' },
  pickup: { label: 'Prise en charge', color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
  in_progress: { label: 'En cours', color: 'bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/40' },
  completed: { label: 'Terminée', color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
  cancelled: { label: 'Annulée', color: 'bg-red-500/20 text-red-300 border border-red-500/40' },
  expired: { label: 'Expirée', color: 'bg-zinc-800 text-zinc-400 border border-zinc-700' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

const StatusBadge = ({ status }) => {
  const cfg = STATUSES[status] || { label: status, color: 'bg-zinc-700 text-zinc-200' };
  return <span data-testid={`ride-status-${status}`} className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
};

const StatusFilter = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="ride-status-filter">
    {[['', 'Toutes'], ...Object.entries(STATUSES).map(([k, v]) => [k, v.label])].map(([k, label]) => (
      <button key={k || 'all'} onClick={() => onChange(k)} data-testid={`ride-filter-${k || 'all'}`}
        className={`px-3 py-1 rounded-full text-xs border transition-colors ${value === k ? 'bg-[#FFD60A] text-black border-[#FFD60A]' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
        {label}
      </button>
    ))}
  </div>
);

const RideRow = ({ r, onCancel }) => (
  <div data-testid={`ride-row-${r.id}`} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 space-y-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-3">
        <StatusBadge status={r.status} />
        <code className="text-xs text-zinc-400" data-testid={`ride-id-${r.id}`}>{r.id}</code>
      </div>
      {r.voidable && (
        <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10"
          onClick={() => onCancel(r)} data-testid={`ride-cancel-btn-${r.id}`}>
          <Ban className="w-4 h-4 mr-1" /> Annuler
        </Button>
      )}
    </div>
    <p className="text-sm text-zinc-200">{r.pickup_address || '—'} <span className="text-zinc-500">→</span> {r.destination_address || '—'}</p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-1 text-xs text-zinc-400">
      <span>Passager : <b className="text-zinc-200">{r.user_name || '—'}</b> {r.user_email && `· ${r.user_email}`}</span>
      <span>Chauffeur : <b className="text-zinc-200">{r.driver_name || (r.broadcast ? 'diffusée, non attribuée' : '—')}</b> {r.vehicle_plate && `· ${r.vehicle_plate}`}</span>
      <span>Créée : {fmt(r.created_at)}</span>
      <span>Acceptée : {fmt(r.accepted_at)} · Prise en charge : {fmt(r.pickup_at)}</span>
      <span>Embarquement : {fmt(r.km_start_at)} · Terminée : {fmt(r.completed_at)}</span>
      {r.status === 'cancelled' && <span className="text-red-300">Annulée {fmt(r.cancelled_at)} par {r.cancelled_by} — {r.cancel_reason || 'sans motif'}</span>}
    </div>
  </div>
);

const CancelDialog = ({ ride, onClose, onDone }) => {
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/rides/${ride.id}/void`, { reason, dry_run: false }, { headers: authHeaders() });
      toast.success('Course annulée (historique conservé, aucun effet financier).');
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || "Annulation refusée par le serveur");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!ride} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="ride-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Annuler cette course ?</DialogTitle>
          <DialogDescription>Course {ride?.id?.slice(0, 8)} — l'enregistrement est conservé, une trace d'audit est créée, aucun gain ni paiement.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ride-cancel-reason" className="bg-zinc-900 border-zinc-700" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="ride-cancel-abort">Retour</Button>
          <Button onClick={confirm} disabled={busy || !reason.trim()} className="bg-red-600 hover:bg-red-500" data-testid="ride-cancel-confirm">
            Confirmer l'annulation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function RideTrackingAdminPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toCancel, setToCancel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/rides/search`, { params: { q: q || undefined, status: status || undefined }, headers: authHeaders() });
      setRides(data.rides);
    } catch (e) {
      toast.error('Recherche impossible');
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 p-6" data-testid="ride-tracking-page">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center gap-4">
          <Link to="/admin"><Button variant="ghost" size="sm" data-testid="back-to-admin-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <h1 className="text-2xl font-bold flex items-center gap-2"><RouteIcon className="w-6 h-6 text-[#FFD60A]" /> Suivi des courses</h1>
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Identifiant, email, nom du passager ou du chauffeur"
            className="bg-zinc-900 border-zinc-700" data-testid="ride-search-input" />
          <Button type="submit" disabled={loading} data-testid="ride-search-btn"><Search className="w-4 h-4 mr-1" /> Rechercher</Button>
          <Button type="button" variant="outline" onClick={load} disabled={loading} data-testid="ride-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </form>
        <StatusFilter value={status} onChange={setStatus} />
        <p className="text-xs text-zinc-500" data-testid="ride-results-count">{rides.length} course(s) — 50 plus récentes au maximum</p>
        <div className="space-y-3">{rides.map((r) => <RideRow key={r.id} r={r} onCancel={setToCancel} />)}</div>
      </div>
      {toCancel && <CancelDialog ride={toCancel} onClose={() => setToCancel(null)} onDone={() => { setToCancel(null); load(); }} />}
    </div>
  );
}
